<script setup lang="ts">
import { useDataTableFilters } from '@/shared/composables/UseDataTableFilters'
import CreateSegmentDialog from '@/features/matching/dialogs/CreateSegmentDialog.vue'
import UpdateSegmentDialog from '@/features/matching/dialogs/UpdateSegmentDialog.vue'
import { deleteSegment, useListAdminUsers, useListSegments } from '@swipe4work/api-orval'
import { AdminPermissionResource, type ListSegmentsSegmentListItemDto, PermissionLevel } from '@swipe4work/models-orval'
import { usePermissions } from '@/shared/composables/UsePermissions'
import { useQueryClient } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { useAuthStore } from '@/shared/stores/AuthStore'

definePage({
  path: '/matching/segments',
  name: '/matching/Segments',
  meta: {
    title: 'Segmenten',
  },
})

const dialog = useDynamicDialog()
const confirm = useConfirm()
const toast = useToast()
const queryClient = useQueryClient()
const authStore = useAuthStore()
const { user } = storeToRefs(authStore)

const { can } = usePermissions()
const canAlgorithmWrite = computed(() => can(AdminPermissionResource.MatchingAlgorithm, PermissionLevel.Write))
const canAlgorithmDelete = computed(() => can(AdminPermissionResource.MatchingAlgorithm, PermissionLevel.Delete))

const { data, isPending, isError } = useListSegments(undefined, { query: { staleTime: 60 * 1000 } })
const { data: adminUsersData } = useListAdminUsers({ query: { staleTime: 5 * 60 * 1000 } })
const segments = computed(() => data.value?.segments ?? [])
const adminUsers = computed(() => adminUsersData.value?.users ?? [])
const currentAdminUserId = computed(() => {
  return adminUsers.value.find(adminUser => adminUser.externalId === user.value?.localAccountId)?.id ?? null
})

type FilterState = {
  visibility: 'all' | 'public' | 'private' | 'own'
  type: 'all' | 'User' | 'Job' | 'Company'
}

const {
  filters,
  draft,
  searchInput,
  search,
  activeFilterCount,
  onFilterPanelShow,
  onFilterApply,
  onFilterClear,
} = useDataTableFilters<FilterState>({
  schema: {
    visibility: 'enum',
    type: 'enum',
  },
  defaultFilters: () => ({
    visibility: 'all',
    type: 'all',
  }),
})

const filteredSegments = computed(() => {
  let result = segments.value

  if (filters.visibility === 'public') {
    result = result.filter(segment => segment.isPublic)
  }
  else if (filters.visibility === 'private') {
    result = result.filter(segment => !segment.isPublic)
  }
  else if (filters.visibility === 'own') {
    if (currentAdminUserId.value === null) {
      return []
    }

    result = result.filter(segment => segment.authorEmployerId === currentAdminUserId.value)
  }

  if (filters.type !== 'all') {
    result = result.filter(segment => segment.type === filters.type)
  }

  if (search.value) {
    const normalizedSearch = search.value.toLowerCase()

    result = result.filter((segment) => {
      const name = segment.name.toLowerCase()
      const description = (segment.description ?? '').toLowerCase()

      return name.includes(normalizedSearch) || description.includes(normalizedSearch)
    })
  }

  return result
})

const segmentTypeLabel: Record<string, string> = {
  User: 'Werkzoekenden',
  Job: 'Vacatures',
  Company: 'Bedrijven',
}

function getAuthorLabel(segment: ListSegmentsSegmentListItemDto) {
  if (segment.authorEmployerId === null || segment.authorEmployerId === undefined) {
    return 'Systeem'
  }

  const author = adminUsers.value.find(adminUser => adminUser.id === segment.authorEmployerId)

  return author?.name ?? `#${segment.authorEmployerId}`
}

function goToCreate() {
  dialog.open(CreateSegmentDialog, {
    props: {
      modal: true,
      closable: true,
      showHeader: false,
      dismissableMask: true,
    },
    data: {
      onSaved: async () => {
        await queryClient.invalidateQueries({ queryKey: [ 'admin', 'segments' ] })
      },
    },
  })
}

function goToEdit(segment: ListSegmentsSegmentListItemDto) {
  dialog.open(UpdateSegmentDialog, {
    props: {
      modal: true,
      closable: true,
      showHeader: false,
      dismissableMask: true,
    },
    data: {
      segmentId: segment.id,
      onSaved: async () => {
        await queryClient.invalidateQueries({ queryKey: [ 'admin', 'segments' ] })
      },
    },
  })
}

function confirmDelete(segment: ListSegmentsSegmentListItemDto) {
  confirm.require({
    header: 'Segment verwijderen',
    message: `Weet je zeker dat je "${segment.name}" wilt verwijderen?`,
    accept: async () => {
      try {
        await deleteSegment(segment.id)
        await queryClient.invalidateQueries({ queryKey: [ 'admin', 'segments' ] })
        toast.add({
          severity: 'success',
          summary: 'Verwijderd',
          detail: 'Segment is verwijderd.',
          life: 3000,
        })
      }
      catch {
        toast.add({
          severity: 'error',
          summary: 'Fout',
          detail: 'Segment kon niet worden verwijderd.',
          life: 3000,
        })
      }
    },
  })
}

const menu = ref()
const selectedSegment = ref<ListSegmentsSegmentListItemDto | null>(null)

const menuItems = computed(() => {
  if (!selectedSegment.value) {
    return []
  }

  return [
    {
      label: 'Bewerken',
      icon: canAlgorithmWrite.value ? 'fa-regular fa-pen' : 'fa-regular fa-lock',
      disabled: !canAlgorithmWrite.value,
      command: () => goToEdit(selectedSegment.value!),
    },
    { separator: true },
    {
      label: 'Verwijderen',
      icon: canAlgorithmDelete.value ? 'fa-regular fa-trash' : 'fa-regular fa-lock',
      disabled: !canAlgorithmDelete.value,
      command: () => confirmDelete(selectedSegment.value!),
    },
  ]
})

function handleClickRow(event: any, segment: ListSegmentsSegmentListItemDto) {
  selectedSegment.value = segment
  menu.value.show(event.originalEvent)
}
</script>

<template>
  <div class="space-y-5">
    <ContextMenu
      ref="menu"
      :model="menuItems"
      @hide="selectedSegment = null"
    />
  
    <Card>
      <template #content>
        <div class="flex flex-col gap-4">
          <div v-if="isPending" class="flex flex-col gap-2">
            <Skeleton v-for="n in 5" :key="n" height="3rem" />
          </div>
          <Message v-else-if="isError" severity="error">
            Er is iets misgegaan bij het laden van segmenten.
          </Message>
          <template v-else>
            <div class="flex items-center justify-between gap-4">
              <h3 class="text-lg font-semibold">
                Segmenten
              </h3>
              <div class="flex items-center gap-3">
                <DataTableFilterPanel
                  :active-filter-count="activeFilterCount"
                  @show="onFilterPanelShow"
                  @apply="onFilterApply"
                  @clear="onFilterClear"
                >
                  <SegmentFilters :draft="draft" />
                </DataTableFilterPanel>

                <IconField class="max-w-xs flex-1">
                  <InputIcon class="fa-regular fa-search" />
                  <InputText
                    v-model="searchInput"
                    placeholder="Zoeken op naam of omschrijving..."
                    class="w-full"
                  />
                  <InputIcon
                    v-if="searchInput"
                    class="fa-regular fa-xmark cursor-pointer"
                    style="pointer-events: auto"
                    @click="searchInput = ''"
                  />
                </IconField>

                <Button
                  v-permission="{ resource: AdminPermissionResource.MatchingAlgorithm, level: PermissionLevel.Write }"
                  label="Nieuw segment"
                  icon="fa-regular fa-plus"
                  @click="goToCreate"
                />
              </div>
            </div>

            <DataTable
              :value="filteredSegments"
              class="w-full"
              row-hover
              @row-click="(e) => handleClickRow(e, e.data)"
            >
              <Column field="name" header="Naam" />
              <Column field="type" header="Type" style="width: 10rem">
                <template #body="{ data: segment }">
                  <Tag :value="segmentTypeLabel[segment.type] ?? segment.type" severity="secondary" />
                </template>
              </Column>
              <Column field="description" header="Omschrijving">
                <template #body="{ data: segment }">
                  <span class="text-sm text-surface-500">{{ segment.description || '—' }}</span>
                </template>
              </Column>
              <Column field="isPublic" header="Zichtbaarheid" style="width: 10rem">
                <template #body="{ data: segment }">
                  <Tag :value="segment.isPublic ? 'Publiek' : 'Privé'" :severity="segment.isPublic ? 'success' : 'warn'" />
                </template>
              </Column>
              <Column field="authorEmployerId" header="Auteur" style="width: 14rem">
                <template #body="{ data: segment }">
                  <span class="text-sm">{{ getAuthorLabel(segment) }}</span>
                </template>
              </Column>
              <Column field="createdAt" header="Aangemaakt" style="width: 10rem">
                <template #body="{ data: segment }">
                  <span class="text-sm">{{ new Date(segment.createdAt).toLocaleDateString('nl-NL') }}</span>
                </template>
              </Column>
              <Column style="width: 3rem">
                <template #body>
                  <i class="fa-regular fa-chevron-right text-surface-400" />
                </template>
              </Column>
            </DataTable>
          </template>
        </div>
      </template>
    </Card>
  </div>
</template>
